import { CircularProgress, Box, Typography } from '@mui/material';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';

const ScopeSVGComponent = (props) => {
  const { 
    svgMap, 
    loader, 
    blocks, 
    setSelectPart, 
    isSelect, 
    selectAll = false, 
    selectPart, 
    scopeLaydownProgress,
    currentLaydownId
  } = props;

  const wrapperRef = useRef(null);
  const blocksRef = useRef(blocks);
  const [svgReady, setSvgReady] = useState(false);
  const eventHandlersRef = useRef(new Map());

  // Update blocks ref when blocks change
  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  const handleBlockClick = useCallback((block) => {
    if (!isSelect) return;

    // Only block if this block belongs to OTHER laydowns (not current laydown)
    if (scopeLaydownProgress && currentLaydownId) {
      const currentLaydownIdStr = currentLaydownId.toString();
      
      // Check if block belongs to OTHER laydowns only
      for (const [laydownId, scopeData] of Object.entries(scopeLaydownProgress)) {
        if (laydownId !== currentLaydownIdStr && scopeData && scopeData[block.id] === -1) {
          // This block belongs to another laydown, don't allow clicking
          return;
        }
      }
    }

    // Allow clicking for current laydown blocks and unassigned blocks
    const blockElement = document.getElementById(block.block_svg_id);
    if (!blockElement) return;

    setSelectPart((prevSelected) => {
      const isAlreadySelected = prevSelected.includes(block.id);

      if (isAlreadySelected) {
        // Unselect - restore original color
        setTimeout(() => applyScopeColorToBlock(block), 0);
        return prevSelected.filter((id) => id !== block.id);
      } else {
        // Select - make yellow
        blockElement.style.fill = "#FFFF00";
        return [...prevSelected, block.id];
      }
    });
    // eslint-disable-next-line
  }, [isSelect, setSelectPart, scopeLaydownProgress, currentLaydownId]);

  const applyScopeColorToBlock = useCallback((block) => {
    const blockElement = document.getElementById(block.block_svg_id);
    if (!blockElement) {
      return;
    }

    // If no scope data, make it white
    if (!scopeLaydownProgress) {
      blockElement.style.fill = "#FFFFFF";
      return;
    }

    const currentLaydownIdStr = currentLaydownId?.toString();
    
    // Check if block exists in any laydown scope
    for (const [laydownId, scopeData] of Object.entries(scopeLaydownProgress)) {
      if (scopeData && scopeData[block.id] === -1) {
        if (laydownId === currentLaydownIdStr) {
          // For current laydown blocks:
          // - If edit mode is false: show yellow
          // - If edit mode is true: show yellow only if selected, white if unselected
          if (!isSelect) {
            blockElement.style.fill = "#FFFF00"; // Always yellow when not in edit mode
          } else {
            // In edit mode, check if block is selected
            if (selectPart && selectPart.includes(block.id)) {
              blockElement.style.fill = "#FFFF00"; // Yellow if selected
            } else {
              blockElement.style.fill = "#FFFFFF"; // White if unselected
            }
          }
        } else if (isSelect) {
          // Other laydown blocks show green only in edit mode
          blockElement.style.fill = "#B4F8D7";
        } else {
          // In non-edit mode, don't color other laydown blocks
          blockElement.style.fill = "#FFFFFF";
        }
        return;
      }
    }
    blockElement.style.fill = "#FFFFFF"; // White for non-scope blocks
  }, [scopeLaydownProgress, currentLaydownId, isSelect, selectPart]);

  // Initialize selectPart with current laydown's blocks when entering edit mode
  useEffect(() => {
    if (isSelect && scopeLaydownProgress && currentLaydownId && blocksRef.current) {
      const currentLaydownIdStr = currentLaydownId.toString();
      const currentLaydownBlocks = [];
      
      // Find blocks that belong to current laydown
      if (scopeLaydownProgress[currentLaydownIdStr]) {
        Object.keys(scopeLaydownProgress[currentLaydownIdStr]).forEach(blockId => {
          if (scopeLaydownProgress[currentLaydownIdStr][blockId] === -1) {
            currentLaydownBlocks.push(parseInt(blockId));
          }
        });
      }
      
      // Set these blocks as initially selected
      setSelectPart(currentLaydownBlocks);
    }
  }, [isSelect, scopeLaydownProgress, currentLaydownId, setSelectPart]);

  const addEventListeners = useCallback(() => {
    if (!wrapperRef.current || !blocksRef.current || blocksRef.current.length === 0) return;

    let allElementsFound = true;

    blocksRef.current.forEach((block) => {
      const blockElement = document.getElementById(block.block_svg_id);
      if (!blockElement) {
        allElementsFound = false;
        return;
      }

      // Apply initial colors
      if (selectAll) {
        blockElement.style.fill = "#FFFF00";
      } else if (selectPart && selectPart.includes(block.id)) {
        blockElement.style.fill = "#FFFF00";
      } else {
        applyScopeColorToBlock(block);
      }

      // Add click handlers
      const clickHandler = () => handleBlockClick(block);

      // Remove existing handlers
      if (eventHandlersRef.current.has(block.block_svg_id)) {
        const existingHandler = eventHandlersRef.current.get(block.block_svg_id);
        blockElement.removeEventListener("click", existingHandler);
      }

      // Add new handlers
      eventHandlersRef.current.set(block.block_svg_id, clickHandler);
      blockElement.addEventListener("click", clickHandler);
    });

    if (allElementsFound) {
      setSvgReady(true);
    } else {
      setTimeout(addEventListeners, 100);
    }
  }, [handleBlockClick, applyScopeColorToBlock, selectAll, selectPart]);

  // Apply scope colors when data changes
  useEffect(() => {
    if (!blocksRef.current || !svgReady || !scopeLaydownProgress) return;

    blocksRef.current.forEach((block) => {
      // Don't override selection colors
      if (selectPart && selectPart.includes(block.id)) return;
      if (selectAll) return;

      applyScopeColorToBlock(block);
    });
  }, [scopeLaydownProgress, currentLaydownId, svgReady, selectPart, selectAll, applyScopeColorToBlock]);

  // Handle selectAll changes
  useEffect(() => {
    if (!blocksRef.current || blocksRef.current.length === 0) return;

    blocksRef.current.forEach((block) => {
      const blockElement = document.getElementById(block.block_svg_id);
      if (!blockElement) return;

      if (selectAll) {
        blockElement.style.fill = "#FFFF00";
      } else {
        applyScopeColorToBlock(block);
      }
    });

    if (selectAll) {
      const allBlockIds = blocksRef.current.map(block => block.id);
      setSelectPart(allBlockIds);
    }
  }, [selectAll, setSelectPart, applyScopeColorToBlock]);

  // Handle selectPart changes (apply colors correctly)
  useEffect(() => {
    if (!blocksRef.current || blocksRef.current.length === 0) return;

    blocksRef.current.forEach((block) => {
      const blockElement = document.getElementById(block.block_svg_id);
      if (!blockElement) return;

      if (selectPart && selectPart.includes(block.id)) {
        // Selected blocks should be yellow
        blockElement.style.fill = "#FFFF00";
      } else if (!selectAll) {
        // Non-selected blocks should use scope colors
        applyScopeColorToBlock(block);
      }
    });
  }, [selectPart, selectAll, applyScopeColorToBlock]);

  // Initialize event listeners
  useEffect(() => {
    if (!svgMap || loader) return;

    // Copy the ref value to avoid stale closure warning
    const currentEventHandlers = eventHandlersRef.current;

    const attemptListeners = () => {
      addEventListeners();
      
      // Retry a few times to ensure DOM is ready
      const retryTimes = [100, 300, 500];
      retryTimes.forEach((delay) => {
        setTimeout(() => {
          if (!svgReady) {
            addEventListeners();
          }
        }, delay);
      });
    };

    setTimeout(attemptListeners, 100);

    return () => {
      // Cleanup event listeners using the copied ref
      const handlersToCleanup = new Map(currentEventHandlers);
      handlersToCleanup.forEach((handler, id) => {
        const blockElement = document.getElementById(id);
        if (blockElement) {
          blockElement.removeEventListener("click", handler);
        }
      });
      currentEventHandlers.clear();
    };
  }, [addEventListeners, svgMap, loader, svgReady]);

  const adjustSvg = useCallback((svg) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = svg;

    const svgElement = tempDiv.querySelector("svg");
    if (svgElement) {
      svgElement.setAttribute("width", "100%");
      svgElement.setAttribute("height", "100%");
      if (!svgElement.hasAttribute("viewBox")) {
        const bbox = svgElement.getBBox();
        svgElement.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
      }

      svgElement.style.backgroundColor = "#FFFFFF";

      const styleTag = document.createElement("style");
      styleTag.textContent = `
        path, polygon, rect { 
          stroke-width: 1px;
        }
      `;
      svgElement.appendChild(styleTag);
    }

    return tempDiv.innerHTML;
  }, []);

  const adjustedSvgMap = useMemo(() => {
    return svgMap ? adjustSvg(svgMap) : '';
  }, [svgMap, adjustSvg]);

  const memoizedContent = useMemo(() => {
    return (
      <Box
        sx={{ 
          display: "flex", 
          cursor: "grab", 
          justifyContent: "center", 
          alignItems: "center",
          position: "relative"
        }}
        style={{ 
          width: '100%', 
          height: 'calc(100vh - 230px)', 
          overflow: 'hidden' 
        }}
      >
        {loader ? (
          <CircularProgress />
        ) : (
          <TransformWrapper
            centerZoomedOut
            minScale={0.2}
            centerOnInit
            limitToBounds
            doubleClick={{ disabled: true }}
            onInit={() => {
              setTimeout(() => {
                addEventListeners();
              }, 200);
            }}
          >
            <TransformComponent wrapperStyle={{ height: "100%", width: "100%" }}>
              <div
                id="scope-svg"
                ref={wrapperRef}
                className="wrapper-component"
                style={{ 
                  width: "100%", 
                  height: "100%", 
                  cursor: isSelect ? "pointer" : "default",
                  position: "relative" 
                }}
                dangerouslySetInnerHTML={{ __html: adjustedSvgMap }}
              />
            </TransformComponent>
          </TransformWrapper>
        )}

        {/* Legend */}
        <Box sx={{ 
          position: "absolute", 
          bottom: 25, 
          left: "50%",
          transform: "translateX(-50%)",
          padding: "12px 16px",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: "#FFFF00",
                  border: "1px solid #ddd",
                  borderRadius: "50%"
                }}
              />
              <Typography fontSize={16} fontWeight={600} color="#878787">
                Blocks assigned to current Laydown
              </Typography>
            </Box>
            {isSelect && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: "#B4F8D7",
                    border: "1px solid #ddd",
                    borderRadius: "50%"
                  }}
                />
                <Typography fontSize={16} fontWeight={600} color="#878787">
                 Blocks assigned to other Laydowns
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Selection instruction */}
        {isSelect && (
          <Box sx={{ 
            position: "absolute", 
            bottom: 85, 
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "8px 16px",
            borderRadius: "4px"
          }}>
            <Typography fontSize={16} fontWeight={600} color="#9ca3af">
              Click on any block to select
            </Typography>
          </Box>
        )}
      </Box>
    );
  }, [loader, adjustedSvgMap, addEventListeners, isSelect]);

  return memoizedContent;
};

export default React.memo(ScopeSVGComponent);